import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { AppModal } from "global/components/AppModal";
import CustomInput from "global/components/CustomInput/CustomInput";
import { handleErrorMessage } from "helpers/methods";
import strings from "global/constants/StringConstants";
import {
  recordHostConsent,
  recordInPersonHostLinkConsent,
} from "./InPersonSigningService";
import inPersonStyles from "./InPersonSigning.styles";

/**
 * Prompts the host for a walk-in signer's name and email at hand-off. Shown
 * when a blank in-person placeholder is launched (or advanced to) and the
 * backend needs the signer's identity to open the session. Only the name is
 * required: a walk-in signer may have no email, and the flow does not need one
 * — nothing is emailed to start an in-person signing. An email, when given,
 * delivers their completed copy and anchors them on the audit trail.
 * The host must also attest that the signer is present and has agreed to sign
 * electronically; the signer gives their own consent on the hand-off card.
 *
 * The host attestation is posted from here — once, before `onSubmit` starts or
 * advances the session — so every caller gets it without repeating the call. A
 * failed post keeps the dialog open and never reaches `onSubmit`: no session
 * may open on an unrecorded consent.
 *
 * The `authoring` variant is a different attestation entirely: the
 * requester's own, taken where the decision is actually made — as a signer is
 * switched to in-person delivery, from inside the logged-in app. Unlike the
 * host's and signer's (per envelope), it is one-time and account-level, so
 * this dialog never posts it itself; the caller supplies `onRecordConsent`
 * (see `useRequesterInPersonConsent`), which hits the dedicated
 * `/sign/send/in-person/requester-consent` endpoint and caches the result so
 * later signers/envelopes never re-ask. This earlier post never lets a launch
 * skip the host's own attestation — `start` / `next` still 406 without it.
 */
interface InPersonHandoffDialogProps {
  open: boolean;
  /** Envelope id the consent is recorded against. */
  requestId: string;
  /**
   * Present only for the public host-link page (`/in-person/host?token=`),
   * which has no login session to authenticate the consent post with. When
   * set, the host attestation goes to the token-scoped endpoint instead of
   * the host-auth one, and `requestId` is not used for the post itself.
   */
  hostToken?: string;
  /** The signed key bound to `hostToken` — required alongside it, present exactly when it is. */
  hostKey?: string;
  /**
   * Which of the three consents this is:
   * - `authoring` — the **requester**, as a signer is switched to in-person
   *   delivery, from inside the logged-in app. Future-tense ("has agreed to
   *   sign"); posts to the same host-consent endpoint as `consent` /
   *   `capture`.
   * - `consent` — the **host**, at launch, attesting the signer is in front of
   *   them. This is the one `start` / `next` require.
   * - `capture` — the host again, when the signer is a blank placeholder, so
   *   their identity is collected alongside that same attestation.
   */
  variant?: "capture" | "consent" | "authoring";
  /**
   * `authoring` only: posts the requester's one-time, account-level
   * attestation and caches it. Required for that variant — without it, the
   * dialog would confirm without recording anything.
   */
  onRecordConsent?: () => Promise<void>;
  /**
   * The signer verifies with an emailed one-time code, so the address they
   * would otherwise be free to skip is mandatory for them.
   */
  emailRequired?: boolean;
  /** Backend 406 message, shown above the fields when present. */
  message?: string;
  initialName?: string;
  initialEmail?: string;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (name: string, email: string) => void;
}

const InPersonHandoffDialog = ({
  open,
  requestId,
  hostToken,
  hostKey,
  variant = "capture",
  onRecordConsent,
  emailRequired = false,
  message,
  initialName,
  initialEmail,
  loading,
  onCancel,
  onSubmit,
}: InPersonHandoffDialogProps) => {
  const theme = useTheme();
  const styles = inPersonStyles(theme);
  const [name, setName] = useState<string>(initialName ?? "");
  const [email, setEmail] = useState<string>(initialEmail ?? "");
  const [consented, setConsented] = useState<boolean>(false);
  const [touched, setTouched] = useState<boolean>(false);
  const [recording, setRecording] = useState<boolean>(false);

  /**
   * Reset the form each time the dialog is (re)opened — the tick included.
   *
   * Nothing is read back from the envelope to pre-tick it. What the host is
   * attesting to is that someone is standing in front of them *now*, so a
   * consent recorded for an earlier hand-off is not an answer to this one: the
   * box starts empty on every session start, and the attestation is posted
   * again each time so the audit trail carries one per hand-off.
   */
  useEffect(() => {
    if (!open) return;
    setName(initialName ?? "");
    setEmail(initialEmail ?? "");
    setConsented(false);
    setTouched(false);
  }, [open, initialName, initialEmail]);

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  // Email is optional — a walk-in signer may not have one, and the envelope
  // then carries no completed copy for them. Anything entered is still checked,
  // and an emailed-OTP signer has to give one: the code has nowhere else to go.
  const emailFormatValid = !trimmedEmail || strings.regex.test(trimmedEmail);
  const emailValid = emailRequired
    ? Boolean(trimmedEmail) && emailFormatValid
    : emailFormatValid;
  const nameError = touched && !trimmedName ? "Name is required" : "";
  const emailError = !touched
    ? ""
    : emailRequired && !trimmedEmail
      ? strings.inPerson.emailRequiredOtp
      : !emailFormatValid
        ? "Please enter a valid email"
        : "";
  const consentError = touched && !consented;
  const isAuthoring = variant === "authoring";
  const isCapture = variant === "capture";
  const canSubmit =
    consented && (!isCapture || (Boolean(trimmedName) && emailValid));

  const handleConfirm = async () => {
    setTouched(true);
    if (!canSubmit) return;
    // `authoring` is the requester's own, one-time, account-level attestation
    // — a different post entirely from the host's, so it never touches
    // `recordHostConsent` / `recordInPersonHostLinkConsent` below.
    if (isAuthoring) {
      if (onRecordConsent) {
        try {
          setRecording(true);
          await onRecordConsent();
        } catch (error: any) {
          handleErrorMessage(error);
          return;
        } finally {
          setRecording(false);
        }
      }
      onSubmit(trimmedName, trimmedEmail);
      return;
    }
    // The host attestation, re-posted fresh on every hand-off regardless of an
    // earlier one: `start` / `next` 406 without it, so this never lets a
    // launch skip the host's own attestation.
    if (hostToken || requestId) {
      try {
        setRecording(true);
        if (hostToken) {
          await recordInPersonHostLinkConsent(hostToken, hostKey || "");
        } else {
          await recordHostConsent(requestId);
        }
      } catch (error: any) {
        // Blocking by design: `start` / `next` return 406 without this
        // consent, so leave the dialog open and let the host retry.
        handleErrorMessage(error);
        return;
      } finally {
        setRecording(false);
      }
    }
    onSubmit(trimmedName, trimmedEmail);
  };

  const body = (
    <Stack spacing={{ xs: 1.25, sm: "14px" }} sx={{ textAlign: "left", mt: "14px" }}>
      <Typography sx={styles.modalSubtitle}>
        {message ||
          (isAuthoring
            ? strings.inPerson.authoringConsentSubtitle
            : isCapture
              ? strings.inPerson.captureSubtitle
              : strings.inPerson.hostConsentSubtitle)}      </Typography>
      {isCapture && (
        <Box>
          <CustomInput
            label="Signer name"
            placeHolder="Enter the signer's full name"
            value={name}
            required
            autoFocus
            labelSpacing={0.5}
            labelSx={styles.modalFieldLabel}
            sx={styles.modalFieldInput}
            onChange={(e: any) => setName(e.target.value.substring(0, 200))}
            error={nameError}
          />
        </Box>
      )}
      {isCapture && (
        <Box>
          <CustomInput
            label="Signer email (optional)"
            placeHolder="name@example.com"
            value={email}
            labelSpacing={0.5}
            labelSx={styles.modalFieldLabel}
            sx={styles.modalFieldInput}
            onChange={(e: any) => setEmail(e.target.value.substring(0, 256))}
            error={emailError}
          />
        </Box>
      )}
      <Box>
        <FormControlLabel
          sx={
            consentError ? styles.consentControlInvalid : styles.consentControl
          }
          control={
            <Checkbox
              id="in_person_host_consent_checkbox"
              checked={consented}
              disabled={loading || recording}
              onChange={(e: any) => setConsented(e.target.checked)}
              sx={styles.consentCheckbox}
              inputProps={{
                "aria-describedby": consentError
                  ? "in_person_host_consent_error"
                  : undefined,
              }}
            />
          }
          label={
            <Typography sx={styles.consentLabel}>
              {isAuthoring
                ? strings.inPerson.authoringConsentLabel
                : strings.inPerson.hostConsentLabel}
            </Typography>
          }
        />
        {consentError && (
          <Typography id="in_person_host_consent_error" sx={styles.consentError}>
            {isAuthoring
              ? strings.inPerson.authoringConsentError
              : strings.inPerson.hostConsentError}
          </Typography>
        )}
      </Box>
      {/* The requester's own tick (authoring) isn't a hand-off moment — only
          the host's (consent/capture) confirm actually starts a session. */}
      {!isAuthoring && (
        <Typography sx={styles.modalNote}>
          {strings.inPerson.handoffPrepNote}
        </Typography>
      )}
    </Stack>
  );

  return (
    <AppModal
      open={open}
      onClose={onCancel}
      title={
        isAuthoring
          ? strings.inPerson.authoringConsentTitle
          : isCapture
            ? strings.inPerson.captureTitle
            : strings.inPerson.hostConsentTitle
      }
      size="md"
      busy={loading || recording}
      // The walk-in signer is standing at the device; a stray scrim click must
      // not drop the hand-off half-way through.
      disableBackdropClose
      primaryLabel={
        isAuthoring
          ? strings.inPerson.authoringConsentConfirm
          : isCapture
            ? strings.inPerson.captureConfirm
            : strings.inPerson.hostConsentConfirm
      }
      onPrimary={handleConfirm}
      primaryLoading={loading || recording}
      primaryDisabled={touched && !canSubmit}
      cancelLabel="Cancel"
      onCancel={onCancel}
    >
      {body}
    </AppModal>  );
};

export default InPersonHandoffDialog;
